import { useEffect, useRef, useState } from 'react';

import { useMytaverseEvent } from '@core/dashboard/providers';
import { useHiddenWhileReconnecting } from '@core/dashboard/hooks/useHiddenWhileReconnecting';

import { BRANDING_TRANSITION_MS } from './constants';

interface ISeen {
  roomId: string | null;
  brandingId: string | null;
}

/**
 * Raises a fresh transition id every time the world changes configuration, and drops it back to
 * null after `BRANDING_TRANSITION_MS`.
 *
 * Derived from the *effective* branding — `currentBrandingId ?? currentRoom.activeBrandingId`, the
 * same resolution `useActiveBrandingId` makes — rather than announced by the switchers, so every
 * path that makes a configuration live is covered by construction: the editor's quick switcher and
 * Admin Tools both go through `useBrandingSwitch`, creating a configuration activates it from a
 * third place, and a remote activation arrives as a room update with no call site at all.
 *
 * Two changes are deliberately NOT a switch: the first resolve of the session (nothing was on
 * screen to change) and a room change (the configuration differs because the room does, and the
 * teleport has its own treatment).
 */
export const useBrandingTransition = (): number | null => {
  const { currentBrandingId, currentRoom } = useMytaverseEvent() || {};
  const reconnecting = useHiddenWhileReconnecting();

  const roomId = currentRoom?.roomId ?? null;
  const brandingId = currentBrandingId ?? currentRoom?.activeBrandingId ?? null;

  const [transitionId, setTransitionId] = useState<number | null>(null);
  const seenRef = useRef<ISeen | null>(null);
  const nextIdRef = useRef(0);

  useEffect(() => {
    const seen = seenRef.current;
    seenRef.current = { roomId, brandingId };

    if (
      !brandingId ||
      !seen ||
      !seen.brandingId ||
      seen.roomId !== roomId ||
      seen.brandingId === brandingId
    ) {
      return;
    }

    nextIdRef.current += 1;
    setTransitionId(nextIdRef.current);
  }, [roomId, brandingId]);

  // Keyed on the id rather than on a boolean: a second switch inside the window has to restart the
  // five seconds, and `setActive(true)` on an already-true flag would not re-run this.
  useEffect(() => {
    if (transitionId === null) {
      return undefined;
    }

    const timer = setTimeout(
      () => setTransitionId(null),
      BRANDING_TRANSITION_MS,
    );

    return () => clearTimeout(timer);
  }, [transitionId]);

  // A seat being replaced is the bigger truth and owns the screen (LoadingCinema, zIndex 30) — this
  // one would cover it, and a reconnect is not a re-skin.
  return reconnecting ? null : transitionId;
};
